/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ReactElement, useEffect, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import { CustomContentGenerator, EventApi, EventContentArg } from '@fullcalendar/core';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import '../../../styles/components/training-calendar.scss';
import axios from 'axios';
import { getLocaleFromHtml } from '~utils/locale';
import { changeWindowUrl, encodeQueryData, getJSONDataFromLocationSearch } from '~utils/url';
import Filter, { filterCellSchema } from '~react/components/Calendar/components/Filter';
import { BasicObject } from '~components/ObjectHelper';
import { z } from 'zod';
import Actions, { actionItemSchema } from '~react/components/Calendar/components/Actions';
import RoomPartCombination from '~react/components/DateTimeRoomPartSelect/components/RoomPartCombination';

export const timeCalendarConfigSchema = z.object({
    apiUrl: z.string(),
    filters: z.array(filterCellSchema).default([]),
    actions: z.array(actionItemSchema).default([]),
    selectUrl: z
        .string()
        .nullable()
        .optional(),
    interval: z
        .object({
            minTime: z
                .string()
                .optional()
                .default('00:00:00'),
            maxTime: z
                .string()
                .optional()
                .default('23:59:59'),
        })
        .default({}),
});

export interface CalendarProps {
    config: z.infer<typeof timeCalendarConfigSchema>;
}

export interface EventsDataType {
    title: string;
    start: string;
    end: string;
    linkUrl: string;
    color: string;
    classNames: string[];
}
const slotDuration = 1000 * 60 * 60; // 1h
const isDateAllowedToBeSelected = (date: Date): boolean => {
    const tomorowDate = new Date();
    tomorowDate.setDate(tomorowDate.getDate() + 1);
    tomorowDate.setHours(0, 0, 0, 0);
    return date.getTime() >= tomorowDate.getTime();
};

export default function TimeCalendar(props: CalendarProps): ReactElement {
    const calendarComponentRef = React.useRef<FullCalendar>(null);

    const [calendarEvents, setCalendarEvents] = useState<EventsDataType[]>([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [filter, setFilter] = useState<BasicObject>({});

    useEffect(() => {
        const searchData: BasicObject = getJSONDataFromLocationSearch('');

        const newFilter = filter;
        props.config.filters.map(item => {
            newFilter[item.name] = item.defaultValue;
        });

        Object.keys(searchData).map(key => {
            newFilter[key] = searchData[key];
        });

        setFilter({ ...newFilter });
    }, []);

    useEffect(() => {
        if (undefined !== filter.week && '' !== filter.week) {
            setCurrentDate(new Date(Date.parse(filter.week + 'T00:00:00.00')));
        }
        loadEvents();
        changeWindowUrl('?' + encodeQueryData(filter));
    }, [filter]);

    useEffect(() => {
        setComponentCalendarDate();
    }, [currentDate]);

    const loadEvents = (): void => {
        setCalendarEvents([]);
        axios
            .get<{ events: EventsDataType[] }>(props.config.apiUrl, {
                params: filter,
            })
            .then(response => {
                if (200 === response.status) {
                    const events = response.data.events;
                    setCalendarEvents(events);
                }
            })
            .catch(error => {
                console.error(error);
            });
    };

    const setComponentCalendarDate = (): void => {
        if (null === calendarComponentRef?.current) {
            return;
        }
        const calendarApi = calendarComponentRef.current.getApi();
        calendarApi.gotoDate(currentDate);
    };

    const eventRender: CustomContentGenerator<EventContentArg> = info => {
        const event: EventApi = info.event;
        const reservedParts = event.extendedProps.reservedParts ?? [];
        const roomParts = event.extendedProps.roomParts ?? [];
        if (roomParts.length !== 0) {
        }

        return (
            <span
                className={'event-inner ' + (roomParts.length > 0 ? '-with-parts' : '')}
                style={{
                    backgroundColor: roomParts.length === 0 ? 'transparent' : 'lightgray',
                }}
                title={event.extendedProps.tooltip ?? event.title}
            >
                <RoomPartCombination
                    roomParts={roomParts}
                    combinationParts={reservedParts}
                    roomColor={event.backgroundColor}
                />
                {event.title.split('\n').map((item, index) => {
                    return <span key={index}>{item}</span>;
                })}
            </span>
        );
    };

    const onFilterChange = (name: string, value: string) => {
        const newFilter = filter;
        newFilter[name] = value;
        setFilter({ ...newFilter });
    };

    const handleSelectDateRange = (dateFrom: Date, dateTo?: Date): void => {
        if (isDateAllowedToBeSelected(dateFrom)) {
            dateTo = dateTo ?? new Date(dateFrom.getTime() + slotDuration);
            window.location.href = `${
                props.config.selectUrl
            }?dateFrom=${dateFrom?.toISOString()}&dateTo=${dateTo?.toISOString()}&roomId=${filter.roomId}`;
        }
    };

    return (
        <div className="training-calendar">
            <Filter cells={props.config.filters} onChange={onFilterChange} values={filter} />
            <FullCalendar
                plugins={[timeGridPlugin, interactionPlugin]}
                initialView="timeGridWeek"
                views={{
                    timeGridWeek: {
                        allDaySlot: false,
                        slotDuration,
                        nowIndicator: true,
                        slotEventOverlap: false,
                    },
                }}
                headerToolbar={{
                    start: '',
                    center: '',
                    end: '',
                }}
                locale={getLocaleFromHtml()}
                firstDay={1}
                eventContent={eventRender}
                events={calendarEvents}
                ref={calendarComponentRef}
                contentHeight="auto"
                selectable={!!props.config.selectUrl}
                slotMinTime={props.config.interval.minTime}
                slotMaxTime={props.config.interval.maxTime}
                select={props.config.selectUrl ? args => handleSelectDateRange(args.start, args.end) : undefined}
                selectAllow={args => isDateAllowedToBeSelected(args.start)}
            />
            {props.config.actions ? <Actions actions={props.config.actions} filter={filter} /> : ''}
        </div>
    );
}
